import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import ConfirmationButton from '../../../ui/confirmationButton/ConfirmationButton';
import LatestBankStatements from './latestBankStatements/LatestBankStatements';
import messages from '../ApplyFlexiLoanFormView.messages';
import styles from '../ApplyFlexiLoanFormView.module.css';
import DocumentRequirementsDE from './documentRequirements/DocumentRequirementsDE';
import DocumentRequirements from './documentRequirements/DocumentRequirements';
import { hasOpenApplication } from '../../../multiLoanDashboard/multiLoanDashboardSelector';
import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../../../multiLoanDashboard/accountsSelector';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { getCurrentStateKey } from '../../../../iwQueryClient/iwQueryClient';
import { RootState } from '@iwProvider/configureStore';
import { TDocumentRequirements } from '../ApplyFlexiLoanFormView';

const mapStateToProps = (state: RootState) => {
    const isRBL = hasOpenApplication(state, REVENUE_BASED_LOAN_PRODUCT_TYPE);
    return {
        approvalRequirementsLoading: () => {
            if (isRBL)
                return state?.revenueBasedLoanDashboard
                    ?.approvalRequirementsLoading;
            return state?.applyFlexiLoanView?.approvalRequirementsLoading;
        },
        submitApplicationLoading: () => {
            if (isRBL)
                return state?.revenueBasedLoanDashboard
                    ?.submitApplicationLoading;
            return state?.applyFlexiLoanView?.submitApplicationLoading;
        }
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

type TApprovalRequirements = {
    submitFailed: boolean;
    flexiLoanDocumentRequirements: TDocumentRequirements;
};

const ApprovalRequirements = ({
    submitFailed,
    flexiLoanDocumentRequirements,
    submitApplicationLoading,
    approvalRequirementsLoading
}: PropsFromRedux & TApprovalRequirements) => {
    const intl = useIntl();
    const stateKey = getCurrentStateKey();

    const { data: amountRequested } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        select: ({ data }) => {
            const requestedProduct = data!.requested_products![0];
            if (requestedProduct.requested_cash_amount !== null) {
                return requestedProduct.requested_cash_amount;
            } else {
                return requestedProduct.amount;
            }
        }
    });

    return (
        <>
            <h4 className={styles.formSubtitle}>
                <FormattedMessage
                    {...messages.documentsTitle}
                    values={{
                        amount: (
                            <strong>
                                {intlFormatCurrency({
                                    intl: intl,
                                    value: amountRequested!,
                                    decimalPlaces: 2
                                })}
                            </strong>
                        )
                    }}
                />
            </h4>

            {getCountry() === 'DE' ? (
                <DocumentRequirementsDE
                    documentRequirements={flexiLoanDocumentRequirements}
                    isLoading={approvalRequirementsLoading()!}
                    hasSubmitFailed={submitFailed}
                />
            ) : (
                <>
                    <LatestBankStatements hasSubmitFailed={submitFailed} />
                    <DocumentRequirements
                        documentRequirements={flexiLoanDocumentRequirements}
                        isLoading={approvalRequirementsLoading()!}
                        hasSubmitFailed={submitFailed}
                    />
                </>
            )}

            <ConfirmationButton
                className={styles.submitBtn}
                data-testid="submit-application"
                type="submit"
                loading={
                    approvalRequirementsLoading() || submitApplicationLoading()
                }
            >
                <FormattedMessage {...messages.submitCTA} />
            </ConfirmationButton>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(ApprovalRequirements);
